// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, finalize, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import type { Empty } from "@bufbuild/protobuf/wkt"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import { DeleteTimeGroupRequestSchema } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type DeleteTimeGroupRpc = (request: Init<typeof DeleteTimeGroupRequestSchema>) => Observable<Empty>

export const deleteTimeGroups = (deleteTimeGroupRpc: DeleteTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteTimeGroups.match),
      mergeMap(({ payload: { request, selectedTimeRowsIds, resetProgress }}) =>
        deleteTimeGroupRpc(request)
          .pipe(
            mergeMap(() => concat(
              of(TimelineSlice.deleteTimeGroups(request.timeGroupIds ?? [])),
              of(TimelineSlice.addActiveTimeRowIds({ timeRowIds: selectedTimeRowsIds })),
            )),
            catchError(handleError),
            finalize(resetProgress),
          )
      ),
    )

const handleError = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_delete_time_groups_failed_precondition",
            message: "Failed to delete timesheet(s). May be out of sync."
          }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_delete_time_groups_failed",
      message: "Failed to delete timesheet(s). Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
    }),
    messageType: "ERROR",
  }))
}

const deleteTimeGroupRpc = (request: Init<typeof DeleteTimeGroupRequestSchema>): Observable<Empty> =>
  executorMutation(() => timelineService.deleteTimeGroup(create(DeleteTimeGroupRequestSchema, request)))

export default deleteTimeGroups(deleteTimeGroupRpc)
